/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { FormikHelpers, useFormik } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unstable_usePrompt } from 'react-router';

import { Button } from '@mui/material';

import { Language } from 'shared';

import { CreateOrganizationStep1 } from '../../../../containers/CreateOrganizationOverlay/components/CreateOrganizationStep1';
import { CreateOrganizationStep2 } from '../../../../containers/CreateOrganizationOverlay/components/CreateOrganizationStep2';
import { Namespaces } from '../../../../i18n';
import { OrganizationApiService } from '../../../../services/OrganizationApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { Organization } from '../../../../types/organization/Organization';
import { UpdateOrganizationRequest } from '../../../../types/organization/UpdateOrganizationRequest';
import { reactToUpdateOrganization } from '../../../../utils/reactions';
import { unprocessibleEntityToObject } from '../../../../utils/unprocessibleEntityToObject';
import { useIsMounted } from '../../../../utils/useIsMounted';
import { useOrganizationPermissions } from '../../../../utils/useOrganizationPermissions';
import { getInitialValues, validationSchema } from './DetailsTab.consts';
import { useDetailsTabStyles } from './DetailsTab.styles';
import { EditOrganizationApiValidationErrors, EditOrganizationValues, ResetApiErrorForField } from './DetailsTab.types';

type DetailsTabProps = {
  organization: Organization;
};

export const DetailsTab = memo<DetailsTabProps>(props => {
  const { organization } = props;

  const classes = useDetailsTabStyles();

  const { t } = useTranslation(Namespaces.OrganizationAdmin);

  const permissions = useOrganizationPermissions(organization.id);

  const [apiErrors, setApiErrors] = useState<EditOrganizationApiValidationErrors>({});

  const initialValues = useMemo(() => getInitialValues(organization), [organization]);

  const isMounted = useIsMounted();

  const onSubmit = (values: EditOrganizationValues, formProps: FormikHelpers<EditOrganizationValues>) => {
    setApiErrors({});
    const editOrganization = async () => {
      try {
        const data: UpdateOrganizationRequest = {
          name: values.name.trim(),
          description: values.description.trim() || null,
          email: values.email.trim() || null,
          companyName: values.companyName.trim(),
          language: values.language.trim() as Language,
          vatNumber: values.vatNumber.trim() || null,
          address: {
            line1: values.address.line1.trim(),
            line2: values.address.line2?.trim() || null,
            line3: values.address.line3?.trim() || null,
            postalCode: values.address.postalCode.trim(),
            city: values.address.city.trim(),
            state: values.address.state.trim() || null,
            countryCode: values.address.countryCode,
          },
        };
        const result = await OrganizationApiService.updateOrganizationById(organization.id, data);
        if (!isMounted.current) {
          return;
        }
        const newOrgData = result.data.payload;
        formProps.setSubmitting(false);
        formProps.resetForm({ values: getInitialValues(newOrgData) });
        void reactToUpdateOrganization({ organizationId: organization.id });
      } catch (e) {
        if (!isMounted.current) {
          return;
        }
        const error = e as ApiClientError;
        const responseData = error.response?.data;
        if (responseData && responseData.message === 'unprocessable_entity') {
          setApiErrors(unprocessibleEntityToObject(responseData.payload));
        }
      }
    };
    void editOrganization();
  };

  const resetApiError: ResetApiErrorForField = useCallback(
    name => {
      if (apiErrors[name]) {
        const nextErrors = { ...apiErrors };
        delete nextErrors[name];
        setApiErrors(nextErrors);
      }
    },
    [apiErrors, setApiErrors],
  );

  const formProps = useFormik<EditOrganizationValues>({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { handleSubmit, isValidating, dirty, resetForm } = formProps;

  unstable_usePrompt({
    when: dirty,
    message: t('management.details_section.unsaved_form_question'),
  });

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit}>
        <CreateOrganizationStep1
          {...formProps}
          apiErrors={apiErrors}
          resetApiError={resetApiError}
          disabled={!permissions.edit_organization}
        />
        <CreateOrganizationStep2
          {...formProps}
          apiErrors={apiErrors}
          resetApiError={resetApiError}
          disabled={!permissions.edit_organization}
        />
        <div className={classes.formButtons}>
          {permissions.edit_organization && (
            <>
              <Button variant="contained" type="submit" disabled={isValidating || !dirty} data-cy="form-save-button">
                {t(`${Namespaces.Common}:form.save`)}
              </Button>
              <Button
                onClick={() => resetForm()}
                variant="outlined"
                disabled={isValidating || !dirty}
                data-cy="form-cancel-button"
              >
                {t(`${Namespaces.Common}:form.cancel`)}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
});
